/** Capture links carry untrusted plain text, never commands, markup or credentials. */
export interface BrowserCapture {
  title: string;
  url: string;
  text: string;
  attribution: string;
  capturedAt: string;
  truncated?: boolean;
}

export const captureLimits = { title: 240, url: 4096, text: 12000, attribution: 240, fragment: 200000 } as const;

export function captureSourceUrl(value: string): string {
  if (!value.trim()) return "";
  if (value.length > captureLimits.url) throw new Error("The source URL is too long (maximum 4,096 characters).");
  let url: URL;
  try { url = new URL(value.trim()); } catch { throw new Error("Use a complete http:// or https:// source URL."); }
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) throw new Error("Source URLs must use HTTP(S), without embedded usernames or passwords.");
  return url.href;
}

export function normalizeCapture(value: Partial<BrowserCapture>, now: string): BrowserCapture {
  for (const field of ["title", "text", "attribution"] as const) {
    if ((value[field] || "").length > captureLimits[field]) throw new Error(`${field === "text" ? "Selected text" : field === "title" ? "Title" : "Attribution"} is too long. Shorten it before saving.`);
  }
  const capturedAt = value.capturedAt || now;
  const day = new Date(`${capturedAt.slice(0, 10)}T00:00:00Z`);
  if (capturedAt.length > 40 || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(capturedAt) || !Number.isFinite(Date.parse(capturedAt)) || !Number.isFinite(day.valueOf()) || day.toISOString().slice(0, 10) !== capturedAt.slice(0, 10)) throw new Error("Use an ISO capture date and time, such as 2026-10-19T09:30:00Z.");
  return {
    title: (value.title || "").replace(/[\r\n\u0000]/g, " ").trim(),
    url: captureSourceUrl(value.url || ""),
    text: (value.text || "").replace(/\r\n?/g, "\n").replace(/\u0000/g, ""),
    attribution: (value.attribution || "").replace(/[\r\n\u0000]/g, " ").trim(),
    capturedAt: new Date(capturedAt).toISOString(),
    truncated: !!value.truncated,
  };
}

export function captureNoteBody(value: BrowserCapture): string {
  if (!value.url && !value.text.trim()) throw new Error("Add a source URL or some selected text before saving.");
  const title = value.title || (value.url ? new URL(value.url).hostname : "Browser capture");
  return [title, value.url ? `Source: ${value.url}` : "", value.text.trim(), `Captured: ${value.capturedAt}${value.attribution ? `\nAttribution (provided, not verified): ${value.attribution}` : ""}\nVia WeirDo browser capture${value.truncated ? "\nCapture was shortened; check the source for the complete text." : ""}`].filter(Boolean).join("\n\n");
}

export function readCaptureFragment(hash: string, now: string): BrowserCapture | null {
  // Other application fragments are not capture requests.
  const fragment = hash.replace(/^#/, "");
  if (!/^capture=1(?:&|$)/.test(fragment)) return null;
  if (fragment.length > captureLimits.fragment) throw new Error("This capture link is too large. Open Browser capture and paste a shorter excerpt.");
  const params = new URLSearchParams(fragment);
  return normalizeCapture({ title: params.get("title") || "", url: params.get("url") || "", text: params.get("text") || "", attribution: params.get("by") || "", capturedAt: params.get("at") || now, truncated: params.get("truncated") === "1" }, now);
}

export function captureLink(applicationUrl: string, value?: BrowserCapture): string {
  const target = new URL(applicationUrl);
  if (!["http:", "https:"].includes(target.protocol) || target.username || target.password) throw new Error("Use the HTTP(S) application address.");
  target.search = "";
  const params = new URLSearchParams({ capture: "1" });
  if (value) {
    params.set("title", value.title); params.set("url", value.url); params.set("text", value.text);
    params.set("by", value.attribution); params.set("at", value.capturedAt);
    if (value.truncated) params.set("truncated", "1");
  }
  target.hash = params.toString();
  return target.href;
}

/** Only this fixed, generated program is executable. Captured strings are URL-encoded data. */
export function captureBookmarklet(applicationUrl: string): string {
  const destination = captureLink(applicationUrl).split("#")[0];
  return `javascript:(()=>{if(!/^https?:$/.test(location.protocol)){alert('Capture is available on HTTP(S) pages.');return;}const t=document.title||'',s=String(window.getSelection()||''),p=new URLSearchParams({capture:'1',title:t.slice(0,${captureLimits.title}),url:location.href,text:s.slice(0,${captureLimits.text}),at:new Date().toISOString(),truncated:t.length>${captureLimits.title}||s.length>${captureLimits.text}?'1':'0'});window.open(${JSON.stringify(destination)}+'#'+p.toString(),'_blank','noopener,noreferrer');})()`;
}
